import { useState, FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Anchor, ChevronDown, Loader2 } from "lucide-react";
import logoWhite from "@/assets/logo-white.svg";
import { saveCreatorRequest } from "@/lib/firebase";
import { usePageSEO } from "@/hooks/use-page-seo";

const NAVY = "#0B1622";

const CreatorRequest = () => {
  usePageSEO({
    title: "Creator Request — Yacht Rock Detroit",
    description: "Yacht Rock Detroit creator request form.",
  });

  const [tickets, setTickets] = useState(2);
  const [charity, setCharity] = useState(false);
  const [charityTickets, setCharityTickets] = useState(1);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const fd = new FormData(form);
    setStatus("saving");
    setErrorMsg("");
    try {
      await saveCreatorRequest({
        firstName: String(fd.get("firstName") || "").trim(),
        lastName: String(fd.get("lastName") || "").trim(),
        email: String(fd.get("email") || "").trim(),
        phone: String(fd.get("phone") || "").trim(),
        instagram: String(fd.get("instagram") || "").trim().replace(/^@/, ""),
        tickets,
        charity,
        charityTickets: charity ? charityTickets : 0,
      });
      setStatus("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("creator request failed", err);
      setStatus("error");
      setErrorMsg("Something went wrong — please try again.");
    }
  };

  const label = "block text-[11px] font-bold uppercase tracking-[0.12em] text-white/55 mb-2";
  const input =
    "w-full bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none transition focus:border-yacht-seaglass focus:ring-2 focus:ring-yacht-seaglass/25";

  const Stepper = ({ value, onChange }: { value: number; onChange: (n: number) => void }) => (
    <div className="grid grid-cols-4 gap-2">
      {[1, 2, 3, 4].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`py-3 rounded-xl font-['Anton'] text-lg border transition ${
            value === n
              ? "bg-yacht-yellow text-yacht-charcoal border-yacht-yellow"
              : "bg-white/[0.04] text-yacht-cream/80 border-white/15 hover:border-white/40"
          }`}
        >
          {n}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen text-yacht-cream flex flex-col items-center px-6 py-10 md:py-16" style={{ backgroundColor: NAVY }}>
      <a href="https://yachtrockdetroit.com" className="mb-8">
        <img src={logoWhite} alt="Yacht Rock Detroit" className="h-16 md:h-20 w-auto" />
      </a>

      <AnimatePresence mode="wait">
        {status === "done" ? (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-lg w-full text-center pt-10"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
            >
              <CheckCircle className="w-16 h-16 text-yacht-seaglass mx-auto mb-6" />
            </motion.div>
            <h1 className="font-['Anton'] uppercase text-3xl md:text-4xl leading-[1] mb-5">
              You're in the <span className="text-yacht-yellow">approval stage</span>
            </h1>
            <p className="text-yacht-cream/80 text-lg leading-relaxed">
              We're reviewing your request now. Once approved, your passes land in your inbox — we'll reach out if we
              need anything.
            </p>
            <div className="mt-7 text-left max-w-sm mx-auto rounded-2xl border border-white/12 bg-white/[0.03] p-5">
              <div className="font-['Anton'] uppercase tracking-wide text-yacht-yellow text-sm mb-3">While you wait — here's the ask:</div>
              <ul className="space-y-2.5 text-yacht-cream/85 text-[15px]">
                <li className="flex gap-2.5"><span className="text-yacht-seaglass">⚓</span> Repost a couple of our posts to your story</li>
                <li className="flex gap-2.5"><span className="text-yacht-seaglass">⚓</span> Make a fun video at the festival</li>
                <li className="flex gap-2.5"><span className="text-yacht-seaglass">⚓</span> Tag <b>@yachtrockdetroit</b> in a post</li>
              </ul>
            </div>
            <p className="mt-6 text-yacht-cream/70">See you on the river ⚓</p>
          </motion.div>
        ) : (
          <div key="form" className="max-w-lg w-full">


            <div className="text-center mb-9">
              <div className="inline-flex items-center gap-1.5 bg-yacht-seaglass text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-xs px-4 py-2 rounded-full border-2 border-white/85 -rotate-2 mb-5">
                <Anchor className="w-4 h-4" /> Creator Request
              </div>
              <h1 className="font-['Anton'] uppercase text-3xl md:text-4xl leading-[1]">
                Request your <span className="text-yacht-yellow">passes</span>
              </h1>
              <p className="mt-4 text-yacht-cream/70 leading-relaxed">
                Drop your info below to request your Yacht Rock Detroit passes. Every request is reviewed before passes
                go out.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={label}>First name *</label>
                  <input name="firstName" required placeholder="First" className={input} />
                </div>
                <div>
                  <label className={label}>Last name *</label>
                  <input name="lastName" required placeholder="Last" className={input} />
                </div>
              </div>
              <div>
                <label className={label}>Email *</label>
                <input name="email" type="email" required placeholder="you@email.com" className={input} />
              </div>
              <div>
                <label className={label}>Phone *</label>
                <input name="phone" type="tel" required placeholder="(313) 555-0123" className={input} />
              </div>
              <div>
                <label className={label}>Instagram username *</label>
                <div className="flex items-center bg-white/[0.04] border border-white/15 rounded-xl overflow-hidden focus-within:border-yacht-seaglass focus-within:ring-2 focus-within:ring-yacht-seaglass/25 transition">
                  <span className="px-3.5 text-white/45 border-r border-white/12">@</span>
                  <input name="instagram" required placeholder="yourhandle" className="flex-1 bg-transparent px-3.5 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none" />
                </div>
              </div>
              <div>
                <label className={label}>How many passes? (max 4) *</label>
                <Stepper value={tickets} onChange={setTickets} />
              </div>

              {/* Charity toggle */}
              <div className="rounded-xl border border-white/12 bg-white/[0.03] p-4">
                <button
                  type="button"
                  onClick={() => setCharity((v) => !v)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <span className="font-semibold text-yacht-cream pr-4">
                    Want to join us the night before? <span className="text-yacht-seaglass">(July 16)</span>
                  </span>
                  <span
                    className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition ${
                      charity ? "bg-yacht-seaglass" : "bg-white/15"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all ${
                        charity ? "left-[1.45rem]" : "left-0.5"
                      }`}
                    />
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setAboutOpen((v) => !v)}
                  className="mt-2 inline-flex items-center gap-1 text-[13px] text-yacht-seaglass"
                >
                  About this event <ChevronDown className={`w-4 h-4 transition ${aboutOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {aboutOpen && (
                    <motion.p
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden text-[13.5px] text-yacht-cream/70 leading-relaxed"
                    >
                      <span className="block pt-3">
                        <b className="text-yacht-cream">MGH Unplugged — Thursday, July 16.</b> An intimate acoustic
                        evening with open bar, benefiting the Detroit Youth Choir. Legends Live, up close. Great for
                        content and a perfect kickoff to the weekend.
                      </span>
                    </motion.p>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {charity && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4">
                        <label className={label}>How many for July 16?</label>
                        <Stepper value={charityTickets} onChange={setCharityTickets} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {status === "error" && <p className="text-red-400 text-sm text-center">{errorMsg}</p>}

              <button
                type="submit"
                disabled={status === "saving"}
                className="w-full bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-lg py-4 rounded-xl shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all disabled:opacity-70 disabled:translate-y-0 flex items-center justify-center gap-2"
              >
                {status === "saving" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                  </>
                ) : (
                  "Submit Request"
                )}
              </button>
            </form>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreatorRequest;
